"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.builder = exports.enumValue = exports.Operation = exports.FragmentSpread = exports.FragmentDefinitionWithVariables = exports.FragmentDefinition = exports.InlineFragment = exports.Field = void 0;
const graphql_1 = require("graphql");
const helpers_1 = require("./helpers");
class FragmentMap extends Map {
    fragments = new Set();
    constructor() {
        super();
    }
    set(name, fragment) {
        const existing = this.get(name);
        if (existing?.name === fragment.name &&
            fragment instanceof FragmentDefinitionWithVariables &&
            existing instanceof FragmentDefinitionWithVariables) {
            const variables = new Set(Object.values(fragment["variables"]).map((v) => v[variableSymbol].name.value));
            for (const v of Object.values(existing["variables"]).map((v) => v[variableSymbol].name.value)) {
                if (!variables.has(v)) {
                    throw new Error(`Fragment with name ${fragment.name} already exists with different variables`);
                }
                variables.delete(v);
            }
            if (variables.size > 0) {
                throw new Error(`Fragment with name ${fragment.name} already exists with different variables`);
            }
        }
        else if (existing &&
            JSON.stringify(existing) !== JSON.stringify(fragment)) {
            const lines = [
                `Fragment with name ${fragment.name} already exists with a different structure:`,
                `Existing:\n${(0, graphql_1.print)(existing.definition())}`,
                `Incoming:\n${(0, graphql_1.print)(fragment.definition())}`,
            ];
            throw new Error(lines.join("\n"));
        }
        this.fragments.add(fragment);
        super.set(fragment.name, fragment);
        return this;
    }
    has(nameOrFragment) {
        if (typeof nameOrFragment === "string") {
            return super.has(nameOrFragment);
        }
        return this.fragments.has(nameOrFragment);
    }
    clear() {
        throw new Error("Cannot clear fragment map");
    }
    delete() {
        throw new Error("Cannot delete from fragment map");
    }
}
function makeValueNode(value) {
    if (value === null) {
        return {
            kind: "NullValue",
        };
    }
    if (typeof value === "string") {
        return {
            block: false,
            kind: "StringValue",
            value,
        };
    }
    if (typeof value === "number") {
        return {
            kind: "IntValue",
            value: value.toString(),
        };
    }
    if (typeof value === "boolean") {
        return {
            kind: "BooleanValue",
            value,
        };
    }
    if (Array.isArray(value)) {
        return {
            kind: "ListValue",
            values: value.map((v) => makeValueNode(v)),
        };
    }
    if (typeof value === "object") {
        if (variableSymbol in value) {
            return value[variableSymbol];
        }
        if (helpers_1.EnumValueSymbol in value) {
            return {
                kind: "EnumValue",
                value: String(value[helpers_1.EnumValueSymbol]),
            };
        }
        return {
            kind: "ObjectValue",
            fields: Object.entries(value).map(([name, value]) => ({
                kind: "ObjectField",
                name: {
                    kind: "Name",
                    value: name,
                },
                value: makeValueNode(value),
            })),
        };
    }
    throw new Error(`Unsupported value: ${value}`);
}
function createSelectionSet(selections) {
    const nodes = [
        {
            kind: "Field",
            arguments: [],
            directives: [],
            name: {
                kind: "Name",
                value: "__typename",
            },
        },
    ];
    nodes.push(...selections.map((s) => {
        if (s instanceof FragmentDefinition ||
            s instanceof FragmentDefinitionWithVariables) {
            return s.spread();
        }
        return s.document();
    }));
    return {
        kind: "SelectionSet",
        selections: nodes,
    };
}
class Selection {
}
class Field extends Selection {
    name;
    args;
    selections;
    _output;
    _alias;
    constructor(name, args, selections) {
        super();
        this.name = name;
        this.args = args;
        this.selections = selections;
    }
    collectFragments(map) {
        for (const selection of this.selections ?? []) {
            selection.collectFragments(map);
        }
    }
    alias(name) {
        this._alias = name;
        return this;
    }
    document() {
        return {
            kind: "Field",
            name: {
                kind: "Name",
                value: this.name,
            },
            directives: [],
            ...(this._alias
                ? {
                    alias: {
                        kind: "Name",
                        value: this._alias,
                    },
                }
                : {}),
            arguments: this.args,
            ...(this.selections
                ? { selectionSet: createSelectionSet(this.selections) }
                : {}),
        };
    }
}
exports.Field = Field;
class InlineFragment extends Selection {
    typeCondition;
    selections;
    _output;
    _possibleTypes;
    constructor(typeCondition, selections) {
        super();
        this.typeCondition = typeCondition;
        this.selections = selections;
    }
    collectFragments(map) {
        for (const selection of this.selections) {
            selection.collectFragments(map);
        }
    }
    document() {
        return {
            kind: "InlineFragment",
            directives: [],
            typeCondition: {
                kind: "NamedType",
                name: {
                    kind: "Name",
                    value: this.typeCondition,
                },
            },
            selectionSet: createSelectionSet(this.selections),
        };
    }
}
exports.InlineFragment = InlineFragment;
class KeyedCache {
    cacheKeyFn;
    cache = undefined;
    constructor(cacheKeyFn) {
        this.cacheKeyFn = cacheKeyFn;
    }
    get() {
        const cacheKey = this.cacheKeyFn?.();
        if (!this.cache) {
            return undefined;
        }
        if (cacheKey === undefined && this.cache.cacheKey === undefined) {
            return this.cache.value;
        }
        if (cacheKey !== undefined &&
            cacheKey.length !== 0 &&
            cacheKey.every((v, i) => v === this.cache.cacheKey[i])) {
            return this.cache.value;
        }
        return undefined;
    }
    set(value) {
        const cacheKey = this.cacheKeyFn?.();
        this.cache = {
            value,
            cacheKey,
        };
    }
}
class FragmentDefinition extends Selection {
    name;
    typeCondition;
    selections;
    _output;
    _possibleTypes;
    cache = new KeyedCache(undefined);
    constructor(name, typeCondition, selections) {
        super();
        this.name = name;
        this.typeCondition = typeCondition;
        this.selections = selections;
    }
    collectFragments(map) {
        if (map.has(this)) {
            return;
        }
        map.set(this.name, this);
        for (const selection of this.selections()) {
            selection.collectFragments(map);
        }
    }
    definition() {
        return {
            kind: "FragmentDefinition",
            name: {
                kind: "Name",
                value: this.name,
            },
            typeCondition: {
                kind: "NamedType",
                name: {
                    kind: "Name",
                    value: this.typeCondition,
                },
            },
            selectionSet: createSelectionSet(this.selections()),
        };
    }
    document() {
        const cached = this.cache.get();
        if (cached) {
            return cached;
        }
        const fragmentMap = new FragmentMap();
        this.collectFragments(fragmentMap);
        const fragments = Array.from(fragmentMap.values());
        const doc = {
            kind: "Document",
            definitions: fragments.map((f) => f.definition()),
        };
        this.cache.set(doc);
        return doc;
    }
    spread() {
        return {
            kind: "FragmentSpread",
            name: {
                kind: "Name",
                value: this.name,
            },
        };
    }
    dynamic(cacheKey) {
        this.cache = new KeyedCache(cacheKey ?? (() => []));
        return this;
    }
    static() {
        this.cache = new KeyedCache(undefined);
        return this;
    }
}
exports.FragmentDefinition = FragmentDefinition;
class FragmentDefinitionWithVariables extends Selection {
    name;
    typeCondition;
    variables;
    builder;
    _variables;
    _output;
    _possibleTypes;
    cache = new KeyedCache(undefined);
    constructor(name, typeCondition, variables, builder) {
        super();
        this.name = name;
        this.typeCondition = typeCondition;
        this.variables = variables;
        this.builder = builder;
    }
    setVariables(variables) {
        const variablesAreEqual = Object.entries(this.variables).every(([name, variable]) => {
            const otherVariable = variables[name];
            if (!otherVariable) {
                return false;
            }
            return (otherVariable[variableSymbol].name.value ===
                variable[variableSymbol].name.value);
        });
        if (variablesAreEqual) {
            return this;
        }
        return new FragmentDefinitionWithVariables(this.name, this.typeCondition, Object.fromEntries(Object.entries(variables).map(([name, variable]) => {
            return [name, variable];
        })), this.builder);
    }
    collectFragments(map) {
        if (map.has(this)) {
            return;
        }
        map.set(this.name, this);
        for (const selection of this.builder(this.variables)) {
            selection.collectFragments(map);
        }
    }
    definition(variableNameOverride = {}) {
        const variables = {
            ...this.variables,
            ...Object.fromEntries(Object.entries(variableNameOverride).map(([name, newName]) => {
                return [
                    newName,
                    {
                        [variableSymbol]: {
                            kind: "Variable",
                            name: {
                                kind: "Name",
                                value: name,
                            },
                        },
                    },
                ];
            })),
        };
        return {
            kind: "FragmentDefinition",
            name: {
                kind: "Name",
                value: this.name,
            },
            typeCondition: {
                kind: "NamedType",
                name: {
                    kind: "Name",
                    value: this.typeCondition,
                },
            },
            selectionSet: createSelectionSet(this.builder(variables)),
        };
    }
    document() {
        const cached = this.cache.get();
        if (cached) {
            return cached;
        }
        const fragmentMap = new FragmentMap();
        this.collectFragments(fragmentMap);
        const doc = {
            kind: "Document",
            definitions: Array.from(fragmentMap.values()).map((f) => f.definition()),
        };
        this.cache.set(doc);
        return doc;
    }
    spread() {
        return {
            kind: "FragmentSpread",
            name: {
                kind: "Name",
                value: this.name,
            },
        };
    }
    dynamic(cacheKey) {
        this.cache = new KeyedCache(cacheKey ?? (() => []));
        return this;
    }
    static() {
        this.cache = new KeyedCache(undefined);
        return this;
    }
}
exports.FragmentDefinitionWithVariables = FragmentDefinitionWithVariables;
class FragmentSpread extends Selection {
    def;
    constructor(def) {
        super();
        this.def = def;
    }
    collectFragments(map) {
        this.def.collectFragments(map);
    }
    document() {
        return {
            kind: "FragmentSpread",
            name: {
                kind: "Name",
                value: this.def.name,
            },
        };
    }
}
exports.FragmentSpread = FragmentSpread;
class Operation {
    name;
    type;
    variableDefinitions;
    selections;
    cache = new KeyedCache(undefined);
    constructor(name, type, variableDefinitions, selections) {
        this.name = name;
        this.type = type;
        this.variableDefinitions = variableDefinitions;
        this.selections = selections;
    }
    document() {
        const cached = this.cache.get();
        if (cached) {
            return cached;
        }
        const fragmentMap = new FragmentMap();
        for (const selection of this.selections()) {
            selection.collectFragments(fragmentMap);
        }
        const document = {
            kind: "Document",
            definitions: [
                ...Array.from(fragmentMap.values()).map((f) => f.definition()),
                {
                    directives: [],
                    kind: "OperationDefinition",
                    name: {
                        kind: "Name",
                        value: this.name,
                    },
                    operation: this.type,
                    variableDefinitions: this.variableDefinitions,
                    selectionSet: createSelectionSet(this.selections()),
                },
            ],
        };
        this.cache.set(document);
        return document;
    }
    /**
     *
     * @param cacheKey A cache key that describes what values are used to build the query. No cache key or an empty array means the query will regenerate every time.
     */
    dynamic(cacheKey) {
        this.cache = new KeyedCache(cacheKey ?? (() => []));
        return this;
    }
    static() {
        this.cache = new KeyedCache(undefined);
        return this;
    }
}
exports.Operation = Operation;
function makeBuilderObject() {
    return new Proxy({}, {
        get(_, prop) {
            if (prop === "__on") {
                const builder = (typeCondition, builder) => {
                    const selections = builder(makeBuilderObject());
                    return new InlineFragment(typeCondition, selections);
                };
                return builder;
            }
            if (prop === "__fragment") {
                const builder = (fragment, variables) => {
                    if (fragment instanceof FragmentDefinitionWithVariables &&
                        variables) {
                        return fragment.setVariables(variables);
                    }
                    return fragment;
                };
                return builder;
            }
            const builder = (arg1, arg2, arg3) => {
                let builder = undefined;
                let args = undefined;
                let alias = undefined;
                if (typeof arg1 === "object") {
                    args = arg1;
                    if (typeof arg2 === "string") {
                        alias = arg2;
                    }
                    else {
                        builder = arg2;
                        alias = arg3;
                    }
                }
                else if (arg1 instanceof Function) {
                    builder = arg1;
                    if (typeof arg2 === "string") {
                        alias = arg2;
                    }
                }
                const argumentNodes = [];
                if (args) {
                    for (const [name, value] of Object.entries(args)) {
                        argumentNodes.push({
                            kind: "Argument",
                            name: {
                                kind: "Name",
                                value: name,
                            },
                            value: makeValueNode(value),
                        });
                    }
                }
                let selections = undefined;
                if (builder) {
                    selections = builder(makeBuilderObject());
                }
                return new Field(prop, argumentNodes, selections);
            };
            return builder;
        },
    });
}
const variableSymbol = Symbol.for("variable");
function enumValue(value) {
    return {
        [helpers_1.EnumValueSymbol]: value,
    };
}
exports.enumValue = enumValue;
function makeOperationBuilder(type) {
    return function builderOperation(name, arg1, arg2) {
        const variableDefinitions = [];
        const variables = {};
        let builder;
        if (typeof arg1 === "object") {
            for (const [name, type] of Object.entries(arg1)) {
                variableDefinitions.push({
                    kind: "VariableDefinition",
                    directives: [],
                    variable: {
                        kind: "Variable",
                        name: {
                            kind: "Name",
                            value: name,
                        },
                    },
                    type: (0, graphql_1.parseType)(type, {
                        noLocation: true,
                    }),
                });
            }
            builder = arg2;
            for (const def of variableDefinitions) {
                variables[def.variable.name.value] = { [variableSymbol]: def.variable };
            }
        }
        else {
            builder = arg1;
        }
        return new Operation(name, type, variableDefinitions, () => builder(makeBuilderObject(), variables));
    };
}
function makeFragmentBuilder() {
    return function (name, typeCondition, arg3, arg4) {
        let builder;
        let variables = undefined;
        if (typeof arg3 === "object") {
            variables = Object.fromEntries(Object.keys(arg3).map((name) => {
                return [
                    name,
                    {
                        [variableSymbol]: {
                            kind: "Variable",
                            name: {
                                kind: "Name",
                                value: name,
                            },
                        },
                    },
                ];
            }));
            builder = arg4;
        }
        else {
            builder = arg3;
        }
        if (variables) {
            return new FragmentDefinitionWithVariables(name, typeCondition, variables, (v) => builder(makeBuilderObject(), v));
        }
        return new FragmentDefinition(name, typeCondition, () => builder(makeBuilderObject(), variables));
    };
}
exports.builder = {
    query: makeOperationBuilder("query"),
    mutation: makeOperationBuilder("mutation"),
    subscription: makeOperationBuilder("subscription"),
    fragment: makeFragmentBuilder(),
};
//# sourceMappingURL=runtime.js.map